import { useMemo } from 'react';
import { useApp } from '../AppProvider';
import { useAuth } from '../../lib/auth';
import { getCurrentUserOrganizationId } from '../../lib/stores/referenceDataCache';
import { useActivitySave } from '../shared/activity-drawer/useActivitySave';
import { resolveComposerEndDate } from '../shared/timeline/composerDueDate';
import { insertDealNoteDirect } from './dealNoteWrites';
import type { EntityAdapter } from '../shared/activity-drawer/types';

/**
 * Salvataggi del composer inline nella cronologia di un'offerta.
 *
 * Stessi percorsi del drawer, nessuno nuovo:
 * - **nota** → `insertDealNoteDirect` (insert diretto su `deal_notes`), esattamente come
 *   `dealDrawerAdapter.saveNote` nel ramo SENZA cambio di stato. La superficie eventi resta
 *   invariata: NIENTE `addDealNote`, quindi nessun `deal:note_created` — l'evento è oggi
 *   dormiente per le note semplici e accenderlo è una decisione di prodotto già registrata
 *   come rimandata, non un effetto collaterale del composer.
 * - **attività** → lo STESSO `useActivitySave` del drawer, con lo stesso `linkageFields`
 *   (`deal_id`/`lead_id`/`client_id`) e lo stesso `afterActivitySaved` no-op dell'adapter
 *   dell'offerta.
 *
 * Per DESIGN (mockup vista 6b) l'attività veloce non tocca il calendario: `syncOptIn` a
 * false fa uscire subito `skipped` entrambe le sync, senza chiamate né toast.
 */
export function useDealComposerSaves(params: {
  dealId: string;
  dealLeadId?: string | null;
  dealClientId?: string | null;
}) {
  const { dealId, dealLeadId, dealClientId } = params;
  const { toast } = useApp();
  const { user } = useAuth();

  const adapter: EntityAdapter = useMemo(
    () => ({
      kind: 'deal',
      entity: { id: dealId },
      clientId: dealClientId ?? undefined,
      entityUrl: () => `${window.location.origin}/deals/${dealId}`,
      linkageFields: () => ({
        deal_id: dealId || null,
        lead_id: dealLeadId ?? null,
        client_id: dealClientId ?? null,
      }),
      defaultAssigneeId: null,
      validateEntityChanges: () => null,
      commitEntityChanges: async () => {},
      saveNote: async (html: string) => {
        await insertDealNoteDirect(dealId, html);
        toast.success('Nota salvata'); // stesso toast del ramo "senza cambio stato" dell'adapter
      },
      // No-op deliberato, come `dealDrawerAdapter`: sulle attività di un'offerta non è mai
      // esistito alcun trigger di workflow automation.
      afterActivitySaved: async () => {},
    }),
    [dealId, dealLeadId, dealClientId, toast]
  );

  const { saveActivity } = useActivitySave(adapter);

  const saveNote = async (html: string) => {
    await adapter.saveNote(html, []);
  };

  const saveActivityQuick = async (input: { title: string; type: string; dueDate: Date; description?: string }) => {
    const organizationId = await getCurrentUserOrganizationId();
    if (!organizationId) {
      toast.error('Organizzazione non disponibile: riprova');
      throw new Error('organization_id non disponibile');
    }
    const saved = await saveActivity({
      activityData: {
        title: input.title,
        type: input.type,
        status: 'pending',
        startDateTime: input.dueDate,
        endDateTime: resolveComposerEndDate(input.dueDate),
        assigned_to: user?.id || null,
        description: input.description ?? '',
      },
      editingActivity: null,
      syncOptIn: false,
      meetingProvider: 'none',
      selectedAttendees: [],
      invitedCommercial: null,
      meetOrganizer: 'agent',
      includeDescriptionInEvent: false,
      organizationId,
      toast,
    });
    if (!saved) throw new Error("Salvataggio dell'attività fallito");
  };

  return { saveNote, saveActivity: saveActivityQuick };
}
